import { useState, useMemo, useEffect } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { InstallSectionCard } from "../InstallSectionCard";
import { FaiByProductTable } from "./FaiByProductTable";
import {
  FAI_PRODUCTS_BY_COMPANY_QUERY,
  FAI_DEPARTMENTS_BY_PRODUCT_WITH_ROLES_QUERY,
  FAIProductsByCompanyQueryPayload,
  FAIDepartmentsByProductQueryPayload,
} from "../../../../../../legacy/queries/FAI";
import { useCompanyPageStore } from "../../../../../../store/companyPageStore";
import { useLazyQuery, useQuery } from "@apollo/client";
import { HgProduct } from "../../../../../../types/taxonomy";
import { COMPANY_SEARCH_CLIENT } from "../../../../../../utils/consts";

type ProductOption = Pick<HgProduct, "id" | "name">;

export const FaiByProduct = () => {
  const company = useCompanyPageStore((state) => state.company);
  const installFilters = useCompanyPageStore((state) => state.installFilters);
  const [selectedProduct, setSelectedProduct] = useState<ProductOption | null>(
    null
  );

  // Query to get all products installed for the company
  const { data: productsData, loading: productsLoading } =
    useQuery<FAIProductsByCompanyQueryPayload>(FAI_PRODUCTS_BY_COMPANY_QUERY, {
      variables: {
        hgId: company!.hgId,
        countryAlpha2s: installFilters.countryAlpha2s,
        productIds: installFilters.productIds,
        vendorIds: installFilters.vendorIds,
        productCategoryIds: installFilters.productCategoryIds,
        productAttributeIds: installFilters.productAttributeIds,
      },
      context: {
        clientName: COMPANY_SEARCH_CLIENT,
      },
    });

  // Query to get FAI data by departments by selected product
  const [fetchFaiData, { data: faiData, loading: faiDataLoading }] =
    useLazyQuery<FAIDepartmentsByProductQueryPayload>(
      FAI_DEPARTMENTS_BY_PRODUCT_WITH_ROLES_QUERY,
      {
        context: {
          clientName: COMPANY_SEARCH_CLIENT,
        },
      }
    );

  // Product options for autocomplete
  const productOptions = useMemo(() => {
    return productsData?.install?.data?.map((item) => item.product) || [];
  }, [productsData]);

  // Set first product as default when data loads and fetch FAI data
  useEffect(() => {
    if (productOptions.length > 0) {
      setSelectedProduct(productOptions[0]);
      fetchFaiData({
        variables: {
          hgId: company!.hgId,
          productId: productOptions[0]?.id,
          per: 10,
        },
      });
    }
  }, [productOptions, fetchFaiData, company]);

  const handleProductChange = (_: unknown, newValue: ProductOption | null) => {
    setSelectedProduct(newValue);
    fetchFaiData({
      variables: {
        hgId: company!.hgId,
        productId: newValue?.id,
        per: 10,
      },
    });
  };

  return (
    <InstallSectionCard
      title="Functional Area Intelligence (FAI) by Product"
      height={720}
      noTitleMargin
      filterKey="FAI_BY_PRODUCT"
    >
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ my: 2, height: 60 }}
      >
        Search for a product below to see its usage across departments. Products
        are listed based on this company's installations and{" "}
        <strong>sorted by intensity</strong>.
      </Typography>

      <Stack direction="row" alignItems="flex-end" spacing={3} mb={4}>
        <Autocomplete
          value={selectedProduct}
          onChange={handleProductChange}
          options={productOptions}
          getOptionLabel={(option) => option.name}
          loading={productsLoading}
          sx={{ width: 300 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Product"
              variant="standard"
              placeholder="Search for a product..."
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          clearOnBlur
          selectOnFocus
          handleHomeEndKeys
        />
      </Stack>

      <FaiByProductTable
        data={faiData?.functionalAreaIntelligenceByUrl?.data}
        loading={faiDataLoading}
        product={{
          id: selectedProduct?.id || 0,
          name: selectedProduct?.name || "",
        }}
      />
    </InstallSectionCard>
  );
};
